import React, { Component } from 'react';
import { resolveImageSrc } from 'utils/cache/imageResolver';
import imageCache from 'utils/cache/imageCache';
import { getImageAuthURLAsync } from 'components/main/common/utils';
import { needsObjectStoragePresign } from 'services/awsUrlProcessor.service';
import EventBus from 'utils/eventBus';

/**
 * CachedImg — drop-in `<img>` with offline-friendly caching.
 *
 * Resolution flow (via imageResolver):
 *   - Outside demo mode: render the apikey'd / pre-signed URL directly.
 *   - Inside demo mode: render a `blob:` URL pulled from IndexedDB (or fetched
 *     once and stored on first encounter). Subsequent loads are guaranteed to
 *     work even when the backend is unreachable.
 *
 * On `<img onError>`:
 *   - For object-storage URLs (AWS / Alibaba / CDN) not yet retried: force-refresh
 *     the pre-sign and try again.
 *   - In demo mode: try the IndexedDB cache as a last resort.
 *   - Otherwise: bubble up to the caller's onError.
 *   - Re-resolves from scratch on EventBus `refresh` (covers apikey arriving after cold start).
 *
 * Renders nothing until the first src resolution completes; this avoids a
 * brief flash where `<img>` mounts with the un-authed URL and immediately errors.
 */
class CachedImg extends Component {
  constructor(props) {
    super(props);
    this.state = { resolvedSrc: null, retried: false };
    this.heldBlobUrl = null;
    this.mounted = false;
  }

  componentDidMount() {
    this.mounted = true;
    this.resolve(this.props.src);
    this.refreshSubscription = EventBus.subscribe('refresh', () => {
      if (this.props.src) {
        this.setState({ retried: false });
        this.resolve(this.props.src);
      }
    });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.src !== this.props.src) {
      this.setState({ retried: false });
      // Do not revoke/clear yet — resolve() swaps src then releases the old blob
      this.resolve(this.props.src);
    }
  }

  componentWillUnmount() {
    this.mounted = false;
    if (this.refreshSubscription && typeof this.refreshSubscription.unsubscribe === 'function') {
      this.refreshSubscription.unsubscribe();
    }
    this.releaseBlob();
  }

  releaseBlob() {
    if (this.heldBlobUrl) {
      URL.revokeObjectURL(this.heldBlobUrl);
      this.heldBlobUrl = null;
    }
  }

  trackBlobIfNeeded(src) {
    if (typeof src === 'string' && src.startsWith('blob:')) {
      this.heldBlobUrl = src;
    }
  }

  async resolve(rawUrl) {
    const src = await resolveImageSrc(rawUrl);
    if (!this.mounted) {
      if (typeof src === 'string' && src.startsWith('blob:')) URL.revokeObjectURL(src);
      return;
    }
    this.releaseBlob();
    this.trackBlobIfNeeded(src);
    this.setState({ resolvedSrc: src });
  }

  handleLoad = (e) => {
    if (this.props.onLoad) this.props.onLoad(e);
  };

  handleError = async (e) => {
    const { src, onError } = this.props;

    if (!this.state.retried && needsObjectStoragePresign(src)) {
      try {
        const refreshed = await getImageAuthURLAsync(src, { forceRefresh: true });
        if (this.mounted && refreshed) {
          this.releaseBlob();
          this.setState({ resolvedSrc: refreshed, retried: true });
          return;
        }
      } catch (_) {
        // fall through to cache fallback
      }
    }

    try {
      const blob = await imageCache.get(src);
      if (blob && this.mounted) {
        const blobUrl = URL.createObjectURL(blob);
        this.releaseBlob();
        this.heldBlobUrl = blobUrl;
        this.setState({ resolvedSrc: blobUrl, retried: true });
        return;
      }
    } catch (_) {
      // fall through to caller's onError
    }

    if (onError) onError(e);
  };

  render() {
    const { resolvedSrc } = this.state;
    if (!resolvedSrc) return null;

    const { onLoad, onError, src, ...rest } = this.props;
    const imgProps = Object.assign({}, rest, {
      src: resolvedSrc,
      onLoad: this.handleLoad,
      onError: this.handleError,
    });
    // eslint-disable-next-line jsx-a11y/alt-text -- alt is forwarded via {...rest} when callers pass it
    return <img {...imgProps} />;
  }
}

export default CachedImg;
